import { apiClient } from '@/sdk/shared/apiClient';
import { getApiBaseUrl } from '@/lib/env';
import type {
  Wallet,
  CreateWalletRequest,
  UpdateBalanceRequest,
  UpdatePendingBalanceRequest,
  WalletResponse,
  DepositRequest,
  DepositRequestWithFile,
  DepositTransaction,
  CreateDepositResponse,
  DepositListResponse,
  ApproveRejectRequest,
  ApproveRejectResponse,
  AdminDepositFilters,
  AdminDepositsResponse,
  AdminDepositsListResponse,
  DepositStats,
  DepositStatsResponse,
  DeleteDepositResponse,
  PaymentProofUrlResponse,
} from './types';

export const walletApi = {
  getWalletBalance: async (clientId: string, tenantSlug?: string): Promise<Wallet> => {
    const params = tenantSlug ? `?tenantSlug=${tenantSlug}` : '';
    const response = await apiClient.get<WalletResponse>(`/wallet/${clientId}${params}`);
    return response.data;
  },

  createWallet: async (data: CreateWalletRequest): Promise<Wallet> => {
    const response = await apiClient.post<WalletResponse>('/wallet', data);
    return response.data;
  },

  updateBalance: async (
    clientId: string,
    data: UpdateBalanceRequest,
    tenantSlug?: string
  ): Promise<Wallet> => {
    const params = tenantSlug ? `?tenantSlug=${tenantSlug}` : '';
    const response = await apiClient.patch<WalletResponse>(
      `/wallet/${clientId}/balance${params}`,
      data
    );
    return response.data;
  },

  updatePendingBalance: async (
    clientId: string,
    data: UpdatePendingBalanceRequest,
    tenantSlug?: string
  ): Promise<Wallet> => {
    const params = tenantSlug ? `?tenantSlug=${tenantSlug}` : '';
    const response = await apiClient.patch<WalletResponse>(
      `/wallet/${clientId}/pending${params}`,
      data
    );
    return response.data;
  },

  getWalletsByTenant: async (
    tenantSlug: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<Wallet[]> => {
    const response = await apiClient.get<{ message: string; data: Wallet[] }>(
      `/wallet/tenant/${tenantSlug}?limit=${limit}&offset=${offset}`
    );
    return response.data;
  },

  // Deposit API endpoints (Client-facing)
  createDepositRequest: async (data: DepositRequest): Promise<DepositTransaction> => {
    const response = await apiClient.post<CreateDepositResponse>('/client/deposit/request', data);
    return response.data;
  },

  // Create deposit request with file upload (multipart/form-data)
  createDepositRequestWithFile: async (
    data: DepositRequestWithFile
  ): Promise<DepositTransaction> => {
    const formData = new FormData();

    // Append all fields to FormData
    formData.append('tenantSlug', data.tenantSlug);
    formData.append('clientId', data.clientId);
    formData.append('amount', data.amount.toString());

    if (data.currency) {
      formData.append('currency', data.currency);
    }
    if (data.paymentMethod) {
      formData.append('paymentMethod', data.paymentMethod);
    }
    if (data.notes) {
      formData.append('notes', data.notes);
    }
    if (data.paymentProof) {
      formData.append('paymentProof', data.paymentProof);
    }

    const token = apiClient.getToken();

    const response = await fetch(`${getApiBaseUrl()}/client/deposit/request`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'An error occurred' }));
      throw {
        status: response.status,
        ...error,
      };
    }

    const result = await response.json();
    return result.data;
  },

  getClientDepositRequests: async (
    clientId: string,
    tenantSlug: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<DepositTransaction[]> => {
    const response = await apiClient.get<DepositListResponse>(
      `/client/deposit/client/${clientId}?tenantSlug=${tenantSlug}&limit=${limit}&offset=${offset}`
    );
    return response.data;
  },

  getPendingDeposits: async (
    tenantSlug: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<DepositTransaction[]> => {
    const response = await apiClient.get<DepositListResponse>(
      `/deposit/pending?tenantSlug=${tenantSlug}&limit=${limit}&offset=${offset}`
    );
    return response.data;
  },

  approveDeposit: async (
    transactionId: string,
    tenantSlug: string,
    adminUserId?: string
  ): Promise<ApproveRejectResponse['data']> => {
    const response = await apiClient.post<ApproveRejectResponse>(
      `/deposit/${transactionId}/approve?tenantSlug=${tenantSlug}`,
      { adminUserId }
    );
    return response.data;
  },

  rejectDeposit: async (
    transactionId: string,
    tenantSlug: string,
    adminUserId?: string
  ): Promise<ApproveRejectResponse['data']> => {
    const response = await apiClient.post<ApproveRejectResponse>(
      `/deposit/${transactionId}/reject?tenantSlug=${tenantSlug}`,
      { adminUserId }
    );
    return response.data;
  },

  // Admin Deposit Management APIs
  getAllDepositsForAdmin: async (
    tenantSlug: string,
    filters: AdminDepositFilters = {},
    limit: number = 50,
    offset: number = 0
  ): Promise<AdminDepositsResponse> => {
    const params = new URLSearchParams({
      tenantSlug,
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (filters.status && filters.status !== 'all') {
      params.append('status', filters.status);
    }
    if (filters.paymentMethod && filters.paymentMethod !== 'all') {
      params.append('paymentMethod', filters.paymentMethod);
    }
    if (filters.search) {
      params.append('search', filters.search);
    }

    const response = await apiClient.get<AdminDepositsListResponse>(
      `/deposit/admin/all?${params.toString()}`
    );
    return response.data;
  },

  getDepositStats: async (tenantSlug: string): Promise<DepositStats> => {
    const response = await apiClient.get<DepositStatsResponse>(
      `/deposit/admin/stats?tenantSlug=${tenantSlug}`
    );
    return response.data;
  },

  // Get payment proof signed URL
  getPaymentProofUrl: async (
    transactionId: string,
    tenantSlug: string
  ): Promise<PaymentProofUrlResponse['data']> => {
    const response = await apiClient.get<PaymentProofUrlResponse>(
      `/deposit/${transactionId}/payment-proof?tenantSlug=${tenantSlug}`
    );
    return response.data;
  },

  // Delete a deposit (admin)
  deleteDeposit: async (
    transactionId: string,
    tenantSlug: string
  ): Promise<DeleteDepositResponse['data']> => {
    const response = await apiClient.delete<DeleteDepositResponse>(
      `/deposit/${transactionId}?tenantSlug=${tenantSlug}`
    );
    return response.data;
  },
};

export default walletApi;
